'use client'

import { BondType } from '@moment/trading-types/reference-data'
import { useCallback } from 'react'

import { type RootState, useAppDispatch } from '@/app/store'
import { IconName } from '@/components/atomic/icon'
import DiversificationConstraint from '@/components/molecules/diversification-constraint'
import {
  type DiversificationConstraintValue,
  DiversificationMode,
} from '@/components/molecules/diversification-constraint/types'
import ModelSectionCard from '@/components/molecules/model-section-card'
import useTieredSelector from '@/hooks/store/useTieredSelector'
import { type ModelBuilder, setModelField } from '@/reducers/model-builder'
import { formatMoney, formatPercentage } from '@/utils/presentation/formatting'

import { DEFAULT_MODEL_BUILDER_VALUES } from './default-values'
import { DiversificationApiField } from './diversification-api-field'
import { ModelConstraintsGrid, ModelConstraintWidth } from './styles'

// Helper to safely get weight range from default values
const getDefaultWeightRange = (
  value: DiversificationConstraintValue | null,
): [number, number] => {
  if (value && value.mode === DiversificationMode.Default) {
    return value.weightRange
  }
  return [0, 100] // fallback
}

export type DiversificationKeys = Extract<
  keyof ModelBuilder,
  | 'weightPerBond'
  | 'weightPerIssuer'
  | 'weightPerSector'
  | 'weightPerState'
  | 'weightPerMaturityYear'
  | 'weightPerFdicCode'
  | 'weightPerInsurer'
  | 'weightPerUltimateBorrower'
  | 'weightPerUltimateParent'
>

export const DIVERSIFICATION_KEYS_TO_API_FIELD: Record<
  DiversificationKeys,
  DiversificationApiField
> = {
  weightPerIssuer: DiversificationApiField.Issuer,
  weightPerSector: DiversificationApiField.Sector,
  weightPerBond: DiversificationApiField.Cusip,
  weightPerState: DiversificationApiField.MunicipalState,
  weightPerMaturityYear: DiversificationApiField.MaturityYear,
  weightPerFdicCode: DiversificationApiField.FdicCode,
  weightPerInsurer: DiversificationApiField.Insurer,
  weightPerUltimateBorrower: DiversificationApiField.UltimateBorrower,
  weightPerUltimateParent: DiversificationApiField.UltimateParent,
} as const

export type DiversificationConstraintModelBuilderStoreValues = {
  [K in DiversificationKeys]: ModelBuilder[K]
}

export const DIVERSIFICATION_SLIDER_DEFAULTS: Record<
  DiversificationKeys,
  {
    defaultValue: [number, number]
    min: number
    max: number
    step: number
  }
> = {
  weightPerIssuer: {
    defaultValue: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerIssuer,
    ),
    min: getDefaultWeightRange(DEFAULT_MODEL_BUILDER_VALUES.weightPerIssuer)[0],
    max: getDefaultWeightRange(DEFAULT_MODEL_BUILDER_VALUES.weightPerIssuer)[1],
    step: 0.01,
  },
  weightPerSector: {
    defaultValue: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerSector,
    ),
    min: getDefaultWeightRange(DEFAULT_MODEL_BUILDER_VALUES.weightPerSector)[0],
    max: getDefaultWeightRange(DEFAULT_MODEL_BUILDER_VALUES.weightPerSector)[1],
    step: 0.01,
  },
  weightPerState: {
    defaultValue: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerState,
    ),
    min: getDefaultWeightRange(DEFAULT_MODEL_BUILDER_VALUES.weightPerState)[0],
    max: getDefaultWeightRange(DEFAULT_MODEL_BUILDER_VALUES.weightPerState)[1],
    step: 0.01,
  },
  weightPerBond: {
    defaultValue: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerBond,
    ),
    min: 0,
    max: 100,
    step: 0.01,
  },
  weightPerMaturityYear: {
    defaultValue: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerMaturityYear,
    ),
    min: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerMaturityYear,
    )[0],
    max: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerMaturityYear,
    )[1],
    step: 0.01,
  },
  weightPerFdicCode: {
    defaultValue: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerFdicCode,
    ),
    min: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerFdicCode,
    )[0],
    max: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerFdicCode,
    )[1],
    step: 1000,
  },
  weightPerInsurer: {
    defaultValue: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerInsurer,
    ),
    min: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerInsurer,
    )[0],
    max: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerInsurer,
    )[1],
    step: 0.01,
  },
  weightPerUltimateBorrower: {
    defaultValue: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerUltimateBorrower,
    ),
    min: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerUltimateBorrower,
    )[0],
    max: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerUltimateBorrower,
    )[1],
    step: 0.01,
  },
  weightPerUltimateParent: {
    defaultValue: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerUltimateParent,
    ),
    min: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerUltimateParent,
    )[0],
    max: getDefaultWeightRange(
      DEFAULT_MODEL_BUILDER_VALUES.weightPerUltimateParent,
    )[1],
    step: 0.01,
  },
} as const

const fieldsToOmitForNonMunicipals = [
  'weightPerState',
  'weightPerInsurer',
  'weightPerUltimateBorrower',
] as const
const fieldsToOmitForNonCDs = ['weightPerFdicCode'] as const
const fieldsToOmitForNonCorporates = ['weightPerUltimateParent'] as const
export const nonApplicableDiversificationFields: Record<
  BondType,
  DiversificationKeys[]
> = {
  [BondType.Agency]: [
    ...fieldsToOmitForNonMunicipals,
    ...fieldsToOmitForNonCDs,
    ...fieldsToOmitForNonCorporates,
  ],
  [BondType.CD]: [
    ...fieldsToOmitForNonMunicipals,
    ...fieldsToOmitForNonCorporates,
  ],
  [BondType.Corporate]: [
    ...fieldsToOmitForNonMunicipals,
    ...fieldsToOmitForNonCDs,
  ],
  [BondType.Government]: [
    ...fieldsToOmitForNonMunicipals,
    ...fieldsToOmitForNonCDs,
    ...fieldsToOmitForNonCorporates,
  ],
  [BondType.MoneyMarket]: [
    ...fieldsToOmitForNonMunicipals,
    ...fieldsToOmitForNonCDs,
    ...fieldsToOmitForNonCorporates,
  ],
  [BondType.Municipal]: [
    'weightPerSector',
    ...fieldsToOmitForNonCDs,
    ...fieldsToOmitForNonCorporates,
  ],
  [BondType.Securitized]: [
    ...fieldsToOmitForNonMunicipals,
    ...fieldsToOmitForNonCDs,
    ...fieldsToOmitForNonCorporates,
  ],
  [BondType.Treasury]: [
    'weightPerIssuer',
    'weightPerSector',
    ...fieldsToOmitForNonMunicipals,
    ...fieldsToOmitForNonCDs,
    ...fieldsToOmitForNonCorporates,
  ],
}

function DiversificationTab() {
  const dispatch = useAppDispatch()
  const model = useTieredSelector(
    (state: RootState) => state.modelBuilder?.model,
  ) as ModelBuilder
  const ladderPolicyEnabled = model?.ladderPolicyEnabled || false

  // Check if a field is applicable to at least one selected asset class (UNION logic)
  const isFieldApplicable = useCallback(
    (field: DiversificationKeys) => {
      const assetClasses = model?.assetClasses || []
      return assetClasses.some(
        (assetClass) =>
          !nonApplicableDiversificationFields[assetClass].includes(field),
      )
    },
    [model?.assetClasses],
  )

  const handleModelFieldChange = useCallback(
    (field: DiversificationKeys) => {
      return (value: DiversificationConstraintValue) => {
        dispatch(setModelField({ field, value }))
      }
    },
    [dispatch],
  )

  const handleReset = useCallback(
    (field: DiversificationKeys) => {
      return () => {
        const defaultValue: DiversificationConstraintValue = {
          mode: DiversificationMode.Default,
          weightRange: DIVERSIFICATION_SLIDER_DEFAULTS[field].defaultValue,
        }
        dispatch(setModelField({ field, value: defaultValue }))
      }
    },
    [dispatch],
  )

  const formatPercentageValue = useCallback((value: number) => {
    return formatPercentage(value, { numDigits: 2 })
  }, [])

  const formatMoneyValue = useCallback((value: number) => {
    return formatMoney(value, { numDigits: 0 })
  }, [])

  return (
    <ModelSectionCard
      title="Diversification constraints"
      description="Specify the weight range for each unique value in a data field."
      icon={IconName.SwatchIcon}
      iconColor="text-moment-red-700 dark:text-moment-red-200"
      iconBackgroundColor="bg-moment-red-100 dark:bg-moment-red-900"
    >
      <ModelConstraintsGrid>
        {isFieldApplicable('weightPerBond') && (
          <ModelConstraintWidth>
            <DiversificationConstraint
              key="weight-per-bond"
              title="Weight per ISIN"
              tooltipText="The minimum and maximum weight for each bond in the portfolio"
              value={model.weightPerBond}
              sliderDefaults={DIVERSIFICATION_SLIDER_DEFAULTS.weightPerBond}
              formatter={formatPercentageValue}
              onValueChange={handleModelFieldChange('weightPerBond')}
              onReset={handleReset('weightPerBond')}
            />
          </ModelConstraintWidth>
        )}

        {isFieldApplicable('weightPerIssuer') && (
          <ModelConstraintWidth>
            <DiversificationConstraint
              key="weight-per-issuer"
              title="Weight per issuer"
              value={model.weightPerIssuer}
              sliderDefaults={DIVERSIFICATION_SLIDER_DEFAULTS.weightPerIssuer}
              formatter={formatPercentageValue}
              onValueChange={handleModelFieldChange('weightPerIssuer')}
              onReset={handleReset('weightPerIssuer')}
            />
          </ModelConstraintWidth>
        )}

        {isFieldApplicable('weightPerSector') && (
          <ModelConstraintWidth>
            <DiversificationConstraint
              key="weight-per-sector"
              title="Weight per sector"
              value={model.weightPerSector}
              sliderDefaults={DIVERSIFICATION_SLIDER_DEFAULTS.weightPerSector}
              formatter={formatPercentageValue}
              onValueChange={handleModelFieldChange('weightPerSector')}
              onReset={handleReset('weightPerSector')}
            />
          </ModelConstraintWidth>
        )}

        {isFieldApplicable('weightPerState') && (
          <ModelConstraintWidth>
            <DiversificationConstraint
              key="weight-per-state"
              title="Weight per state"
              value={model.weightPerState}
              sliderDefaults={DIVERSIFICATION_SLIDER_DEFAULTS.weightPerState}
              formatter={formatPercentageValue}
              onValueChange={handleModelFieldChange('weightPerState')}
              onReset={handleReset('weightPerState')}
            />
          </ModelConstraintWidth>
        )}

        {isFieldApplicable('weightPerMaturityYear') && !ladderPolicyEnabled && (
          <ModelConstraintWidth>
            <DiversificationConstraint
              key="weight-per-maturity-year"
              title="Weight per maturity year"
              value={model.weightPerMaturityYear}
              sliderDefaults={
                DIVERSIFICATION_SLIDER_DEFAULTS.weightPerMaturityYear
              }
              formatter={formatPercentageValue}
              onValueChange={handleModelFieldChange('weightPerMaturityYear')}
              onReset={handleReset('weightPerMaturityYear')}
            />
          </ModelConstraintWidth>
        )}

        {isFieldApplicable('weightPerFdicCode') && (
          <ModelConstraintWidth>
            <DiversificationConstraint
              key="weight-per-fdic-code"
              title="Market value per FDIC issuer"
              value={model.weightPerFdicCode}
              sliderDefaults={DIVERSIFICATION_SLIDER_DEFAULTS.weightPerFdicCode}
              formatter={formatMoneyValue}
              onValueChange={handleModelFieldChange('weightPerFdicCode')}
              onReset={handleReset('weightPerFdicCode')}
            />
          </ModelConstraintWidth>
        )}

        {isFieldApplicable('weightPerInsurer') && (
          <ModelConstraintWidth>
            <DiversificationConstraint
              key="weight-per-insurer"
              title="Weight per insurer"
              value={model.weightPerInsurer}
              sliderDefaults={DIVERSIFICATION_SLIDER_DEFAULTS.weightPerInsurer}
              formatter={formatPercentageValue}
              onValueChange={handleModelFieldChange('weightPerInsurer')}
              onReset={handleReset('weightPerInsurer')}
            />
          </ModelConstraintWidth>
        )}

        {isFieldApplicable('weightPerUltimateBorrower') && (
          <ModelConstraintWidth>
            <DiversificationConstraint
              key="weight-per-ultimate-borrower"
              title="Weight per ultimate borrower"
              value={model.weightPerUltimateBorrower}
              sliderDefaults={
                DIVERSIFICATION_SLIDER_DEFAULTS.weightPerUltimateBorrower
              }
              formatter={formatPercentageValue}
              onValueChange={handleModelFieldChange(
                'weightPerUltimateBorrower',
              )}
              onReset={handleReset('weightPerUltimateBorrower')}
            />
          </ModelConstraintWidth>
        )}

        {isFieldApplicable('weightPerUltimateParent') && (
          <ModelConstraintWidth>
            <DiversificationConstraint
              key="weight-per-ultimate-parent"
              title="Weight per ultimate parent"
              value={model.weightPerUltimateParent}
              sliderDefaults={
                DIVERSIFICATION_SLIDER_DEFAULTS.weightPerUltimateParent
              }
              formatter={formatPercentageValue}
              onValueChange={handleModelFieldChange('weightPerUltimateParent')}
              onReset={handleReset('weightPerUltimateParent')}
            />
          </ModelConstraintWidth>
        )}
      </ModelConstraintsGrid>
    </ModelSectionCard>
  )
}

// Dead UI retained for live constants in this file; unexported for knip.
void DiversificationTab
